/**
 * The user's theme preference: colour mode, accent, and which of "the practice's brand colour" vs
 * "my own accent pick" actually governs the accent.
 *
 * Pure over plain values and a `Storage`-shaped object, so every branch is covered by
 * `preference.test.ts` with no DOM. The DOM half — applying the attributes and verifying the
 * result — lives in `ThemeProvider.tsx` and `guard.ts`.
 *
 * `'system'` is a stored *preference*, not a mode. Resolving it needs the OS setting, which is
 * live state (a user can flip it while the app is open), so it is resolved at render time from a
 * `matchMedia` subscription rather than written down. An explicit `'light'` or `'dark'` ignores the
 * OS outright, which is correct for those two — the distinction only matters for `'system'`.
 *
 * The preference deliberately lives in `localStorage` rather than on the server profile. It has to
 * apply on the **login page**, before any session exists — a user who works in the dark should not
 * have to sign in through a white screen first. A server-side per-user preference is a reasonable
 * later addition layered on top; it cannot replace this.
 *
 * **`accentSource` (`BACKLOG.md` B4's "org brand" item).** A practice may set its own brand colour
 * (`server/src/orgs/brandingService.ts`, `web/src/theme/orgAccent.ts`) — per
 * `claude-kit/rules/invariants.md`'s "Commercial products let the customer brand it," that colour is
 * the *default* a member sees, and an individual override is the exception a member opts into, not
 * the other way round. `'org'` (the default) means "show the practice's colour if it has one, the
 * closed-enum `accent` field otherwise"; `'personal'` means "show `accent`, because this member
 * explicitly picked it via `ThemeMenu.tsx`." `ThemeProvider.tsx`'s `resolveAccentSource` is where
 * this composes with a practice's own `allowUserColorPicker` toggle — a practice that disallows the
 * override always wins regardless of what is stored here, which is why this file alone cannot
 * resolve the final accent and does not try to.
 */

import { type Accent, DEFAULT_ACCENT, isAccent } from './palette.ts';

export const COLOR_MODES = ['system', 'light', 'dark'] as const;
export type ColorMode = (typeof COLOR_MODES)[number];

export const DEFAULT_COLOR_MODE: ColorMode = 'system';

export function isColorMode(value: string): value is ColorMode {
  return (COLOR_MODES as readonly string[]).includes(value);
}

/** Which of the practice's brand colour or this member's own pick governs the accent. See this
 * file's own header for the full reasoning. */
export const ACCENT_SOURCES = ['org', 'personal'] as const;
export type AccentSource = (typeof ACCENT_SOURCES)[number];

/** `'org'` — defer to the practice's colour when it has one — matching the invariant that a
 * practice's own choice is the default a member sees, not an opt-in. */
export const DEFAULT_ACCENT_SOURCE: AccentSource = 'org';

export function isAccentSource(value: string): value is AccentSource {
  return (ACCENT_SOURCES as readonly string[]).includes(value);
}

/**
 * Storage keys. Three keys rather than one JSON blob: a corrupt or partially-written value then
 * costs one preference instead of all three, and the bootstrap script in `index.html` can read them
 * without parsing.
 */
export const MODE_STORAGE_KEY = 'emr:theme-mode';
export const ACCENT_STORAGE_KEY = 'emr:theme-accent';
export const ACCENT_SOURCE_STORAGE_KEY = 'emr:theme-accent-source';

export interface ThemePreference {
  readonly mode: ColorMode;
  readonly accent: Accent;
  readonly accentSource: AccentSource;
}

export const DEFAULT_PREFERENCE: ThemePreference = {
  mode: DEFAULT_COLOR_MODE,
  accent: DEFAULT_ACCENT,
  accentSource: DEFAULT_ACCENT_SOURCE,
};

/**
 * The subset of `Storage` this module uses. Narrower than `Storage` so a test can supply a plain
 * object and so nothing here can reach `clear()` and take out an unrelated key.
 */
export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Read the stored preference, falling back per-field.
 *
 * Every failure mode lands on a default rather than throwing: `localStorage` itself throws in some
 * privacy modes, a value can be absent, and a value can be a name this build no longer recognises
 * (a renamed accent, or something written by a different app on the same origin). None of those is
 * worth an error screen, and all of them are worth *not* trusting.
 */
export function readPreference(storage: PreferenceStorage): ThemePreference {
  let mode: ColorMode = DEFAULT_COLOR_MODE;
  let accent: Accent = DEFAULT_ACCENT;
  let accentSource: AccentSource = DEFAULT_ACCENT_SOURCE;

  try {
    const storedMode = storage.getItem(MODE_STORAGE_KEY);
    if (storedMode !== null && isColorMode(storedMode)) mode = storedMode;

    const storedAccent = storage.getItem(ACCENT_STORAGE_KEY);
    if (storedAccent !== null && isAccent(storedAccent)) accent = storedAccent;

    const storedAccentSource = storage.getItem(ACCENT_SOURCE_STORAGE_KEY);
    if (storedAccentSource !== null && isAccentSource(storedAccentSource)) {
      accentSource = storedAccentSource;
    }
  } catch {
    return DEFAULT_PREFERENCE;
  }

  return { mode, accent, accentSource };
}

/** Persist a preference. A storage failure is not worth failing the interaction over. */
export function writePreference(storage: PreferenceStorage, preference: ThemePreference): void {
  try {
    storage.setItem(MODE_STORAGE_KEY, preference.mode);
    storage.setItem(ACCENT_STORAGE_KEY, preference.accent);
    storage.setItem(ACCENT_SOURCE_STORAGE_KEY, preference.accentSource);
  } catch {
    // Non-fatal: the preference still applies for this page's lifetime.
  }
}

/**
 * The attributes to put on `<html>` for a given preference's closed-enum half.
 *
 * `mode: 'system'` yields `theme: null`, meaning *remove the attribute* — not `data-theme="system"`.
 * The CSS in `tokens.css` has no rule for a `system` value, and leaving a meaningless attribute in
 * place would make the DOM claim a state the stylesheet does not implement.
 *
 * Always computed from `preference.accent`, regardless of `accentSource` — `data-accent` is what
 * drives `tokens.css`'s closed-enum accent blocks, and stays set even while an org override is
 * active, so the moment that override is cleared (the practice removes its colour, or this member's
 * source flips back to `'personal'`) the closed-enum accent underneath is already correct with no
 * extra step. `ThemeProvider.tsx`'s `applyToDocument` is what layers an org override on top via
 * inline custom-property writes; this function knows nothing about that layer.
 */
export function themeAttributes(preference: ThemePreference): {
  readonly theme: 'light' | 'dark' | null;
  readonly accent: Accent;
} {
  return {
    theme: preference.mode === 'system' ? null : preference.mode,
    accent: preference.accent,
  };
}
